import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../axios';

export const loginUser = createAsyncThunk('auth/login', async (creds, { rejectWithValue }) => {
  try {
    const { data } = await api.post('/auth/login', creds);
    return data;
  } catch (e) {
    return rejectWithValue(e.response?.data?.message || 'Login failed');
  }
});

export const registerUser = createAsyncThunk('auth/register', async (payload, { rejectWithValue }) => {
  try {
    const { data } = await api.post('/auth/register', payload);
    return data;
  } catch (e) {
    return rejectWithValue(e.response?.data?.message || 'Registration failed');
  }
});

export const socialAuth = createAsyncThunk('auth/social', async (payload, { rejectWithValue }) => {
  try {
    const { data } = await api.post('/auth/social-auth', payload);
    return data;
  } catch (e) {
    return rejectWithValue(e.response?.data?.message || 'Social sign-in failed');
  }
});

export const fetchProfile = createAsyncThunk('auth/profile', async (_, { rejectWithValue }) => {
  try {
    const { data } = await api.get('/auth/profile');
    return data;
  } catch (e) {
    return rejectWithValue(e.response?.data?.message || 'Failed to load profile');
  }
});

export const saveOnboarding = createAsyncThunk('auth/onboarding', async (payload, { rejectWithValue }) => {
  try {
    const typeMap = { explorer: 'individual', null: 'individual', undefined: 'individual' };
    const userType = typeMap[payload.userType] ?? payload.userType ?? 'individual';
    const { data } = await api.patch('/auth/onboarding', {
      user_type: userType,
      business_name: payload.businessName,
      business_email: payload.businessEmail,
      region: payload.region,
      usecase: payload.usecase,
    });
    return { ...data, localPayload: payload };
  } catch (e) {
    // If backend call fails (e.g. not logged in yet), fall back to local-only save
    return { localPayload: payload };
  }
});

export const logoutUser = createAsyncThunk('auth/logout', async (_, { getState, rejectWithValue }) => {
  try {
    const { auth } = getState();
    if (auth.refreshToken) {
      await api.post('/auth/logout', { refreshToken: auth.refreshToken });
    }
  } catch (_) {}
});

const authSlice = createSlice({
  name: 'auth',
  initialState: {
    user: null,
    accessToken: null,
    refreshToken: null,
    requiresVerification: false,
    onboardingComplete: false,
    userType: null,
    loading: false,
    error: null,
  },
  reducers: {
    clearError: (state) => { state.error = null; },
    setTokens: (state, { payload }) => {
      state.accessToken = payload.accessToken;
      if (payload.refreshToken) state.refreshToken = payload.refreshToken;
    },
    clearAuth: (state) => {
      state.user = null;
      state.accessToken = null;
      state.refreshToken = null;
      state.requiresVerification = false;
      state.onboardingComplete = false;
    },
    completeOnboarding: (state, { payload }) => {
      state.onboardingComplete = true;
      state.userType = payload.userType;
      if (payload.businessName && state.user) {
        state.user = { ...state.user, businessName: payload.businessName, region: payload.region, usecase: payload.usecase };
      }
    },
  },
  extraReducers: (builder) => {
    const pending = (state) => { state.loading = true; state.error = null; };
    const rejected = (state, { payload }) => { state.loading = false; state.error = payload; };

    builder
      .addCase(loginUser.pending, pending)
      .addCase(loginUser.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.accessToken = payload.tokens?.access?.token;
        state.refreshToken = payload.tokens?.refresh?.token;
        state.user = payload.user;
        state.requiresVerification = payload.requiresVerification || false;
        state.onboardingComplete = !!payload.user?.onboarding_complete;
      })
      .addCase(loginUser.rejected, rejected)

      .addCase(registerUser.pending, pending)
      .addCase(registerUser.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.accessToken = payload.tokens?.access?.token;
        state.refreshToken = payload.tokens?.refresh?.token;
        state.user = payload.user;
        state.requiresVerification = payload.requiresVerification || true;
      })
      .addCase(registerUser.rejected, rejected)

      .addCase(socialAuth.pending, pending)
      .addCase(socialAuth.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.accessToken = payload.tokens?.access?.token;
        state.refreshToken = payload.tokens?.refresh?.token;
        state.user = payload.user;
        state.onboardingComplete = !!payload.user?.onboarding_complete;
      })
      .addCase(socialAuth.rejected, rejected)

      .addCase(fetchProfile.fulfilled, (state, { payload }) => {
        // Profile returns { user: {...}, businesses: [...], activeBusiness: { id, ... } }
        const bizId = payload.activeBusiness?.id?.toString() || payload.businesses?.[0]?.id?.toString() || null;
        const baseUser = payload.user || payload;
        state.user = { ...state.user, ...baseUser, ...(bizId ? { businessId: bizId } : {}) };
        if (baseUser.onboarding_complete != null) {
          state.onboardingComplete = !!baseUser.onboarding_complete;
        }
      })

      .addCase(saveOnboarding.fulfilled, (state, { payload }) => {
        const p = payload.localPayload;
        state.onboardingComplete = true;
        state.userType = p.userType;
        const bizId = payload.business?.id?.toString() || null;
        const userPatch = {
          ...(p.businessName ? { businessName: p.businessName, region: p.region, usecase: p.usecase } : {}),
          ...(payload.user || {}),
          ...(bizId ? { businessId: bizId } : {}),
        };
        if (state.user) state.user = { ...state.user, ...userPatch };
      })

      .addCase(logoutUser.fulfilled, (state) => {
        state.user = null;
        state.accessToken = null;
        state.refreshToken = null;
        state.requiresVerification = false;
        state.onboardingComplete = false;
      });
  },
});

export const { clearError, setTokens, clearAuth, completeOnboarding } = authSlice.actions;
export default authSlice.reducer;
